import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { safeGetItem, safeSetItem } from '@/utils/safeStorage';
import { ThemeContext, type Theme } from './theme-context';

const THEME_STORAGE_KEY = 'xkey-theme';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = safeGetItem(THEME_STORAGE_KEY) as Theme | null;
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    // Match index.html: unsaved preference stays light. Following OS dark here used to
    // flip portals to dark after first paint and persist it, with no obvious way back.
    return 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.add('light');
    }
    safeSetItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const value = useMemo(() => ({ theme, toggleTheme, setTheme }), [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export { ThemeContext } from './theme-context';
